import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Info,
  AlertTriangle,
  Lightbulb,
  CheckCircle2,
  XCircle,
  ImageOff,
} from "lucide-react";
import { cn } from "@/lib/utils";

/* ─────────────────────────────────────────────────────────────────────
 * 1. InfoBox — encadré bleu informatif
 * ──────────────────────────────────────────────────────────────────── */
export function InfoBox({ title, children, className }) {
  return (
    <aside
      role="note"
      data-testid="tutorial-info-box"
      className={cn(
        "rounded-lg border-l-4 border-brand-blue bg-brand-blue/10 p-4 dark:bg-brand-blue/15",
        className
      )}
    >
      <div className="flex items-start gap-3">
        <Info className="mt-0.5 h-5 w-5 shrink-0 text-brand-blue dark:text-brand-gold" />
        <div className="min-w-0 flex-1">
          {title && (
            <div className="mb-1 text-sm font-bold text-brand-blue dark:text-brand-gold">
              {title}
            </div>
          )}
          <div className="text-sm leading-relaxed text-foreground/90">
            {children}
          </div>
        </div>
      </div>
    </aside>
  );
}

/* ─────────────────────────────────────────────────────────────────────
 * 2. WarningBox — encadré orange d'avertissement
 * ──────────────────────────────────────────────────────────────────── */
export function WarningBox({ title, children, className }) {
  return (
    <aside
      role="alert"
      aria-live="polite"
      data-testid="tutorial-warning-box"
      className={cn(
        "rounded-lg border-l-4 border-amber-500 bg-amber-500/10 p-4 dark:bg-amber-500/15",
        className
      )}
    >
      <div className="flex items-start gap-3">
        <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600 dark:text-amber-400" />
        <div className="min-w-0 flex-1">
          {title && (
            <div className="mb-1 text-sm font-bold text-amber-700 dark:text-amber-300">
              {title}
            </div>
          )}
          <div className="text-sm leading-relaxed text-foreground/90">
            {children}
          </div>
        </div>
      </div>
    </aside>
  );
}

/* ─────────────────────────────────────────────────────────────────────
 * 3. TipBox — encadré vert d'astuce
 * ──────────────────────────────────────────────────────────────────── */
export function TipBox({ title, children, className }) {
  return (
    <aside
      role="note"
      data-testid="tutorial-tip-box"
      className={cn(
        "rounded-lg border-l-4 border-emerald-500 bg-emerald-500/10 p-4 dark:bg-emerald-500/15",
        className
      )}
    >
      <div className="flex items-start gap-3">
        <Lightbulb className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600 dark:text-emerald-400" />
        <div className="min-w-0 flex-1">
          {title && (
            <div className="mb-1 text-sm font-bold text-emerald-700 dark:text-emerald-300">
              {title}
            </div>
          )}
          <div className="text-sm leading-relaxed text-foreground/90">
            {children}
          </div>
        </div>
      </div>
    </aside>
  );
}

/* ─────────────────────────────────────────────────────────────────────
 * 4. Step — étape numérotée
 * ──────────────────────────────────────────────────────────────────── */
export function Step({ number, title, children, className }) {
  return (
    <div
      data-testid={`tutorial-step-${number}`}
      className={cn("flex items-start gap-4", className)}
    >
      <span
        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-brand-gold text-sm font-bold text-background shadow-sm"
        aria-hidden="true"
      >
        {number}
      </span>
      <div className="min-w-0 flex-1 pt-1">
        <div className="font-semibold leading-snug">{title}</div>
        {children && (
          <div className="mt-1 text-sm leading-relaxed text-muted-foreground">
            {children}
          </div>
        )}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────
 * 5. AnnotatedScreen — image + bulles numérotées + légendes
 *    Fallback placeholder si l'image est absente.
 * ──────────────────────────────────────────────────────────────────── */
export function AnnotatedScreen({ src, alt, annotations = [], caption }) {
  const [broken, setBroken] = useState(false);
  const fallbackAlt =
    alt ||
    (src
      ? `Capture d'écran à venir (${src.split("/").pop().replace(/\.[^.]+$/, "")})`
      : "Capture d'écran à venir");

  return (
    <figure
      data-testid="tutorial-annotated-screen"
      className="overflow-hidden rounded-lg border border-border bg-muted/40"
    >
      <div className="relative w-full bg-[#0f172a] [touch-action:pinch-zoom]">
        {broken || !src ? (
          <div
            className="flex aspect-[16/9] flex-col items-center justify-center gap-2 text-muted-foreground/70"
            role="img"
            aria-label={fallbackAlt}
          >
            <ImageOff className="h-8 w-8" aria-hidden="true" />
            <div className="text-xs italic">
              Capture d'écran à venir
              {src && (
                <>
                  {" "}
                  · <code className="text-[10px]">{src}</code>
                </>
              )}
            </div>
          </div>
        ) : (
          <img
            src={src}
            alt={fallbackAlt}
            loading="lazy"
            onError={() => setBroken(true)}
            className="block w-full"
          />
        )}
        {!broken &&
          annotations.map((a, i) => (
            <span
              key={i}
              className="absolute flex h-8 w-8 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-brand-gold text-xs font-black text-background shadow-md ring-2 ring-background"
              style={{ left: `${a.x}%`, top: `${a.y}%` }}
              aria-hidden="true"
            >
              {a.label ?? i + 1}
            </span>
          ))}
      </div>
      {annotations.length > 0 && (
        <ol className="space-y-1 border-t border-border bg-card px-4 py-3 text-sm">
          {annotations.map((a, i) => (
            <li key={i} className="flex items-start gap-2">
              <span className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-brand-gold text-[10px] font-bold text-background">
                {a.label ?? i + 1}
              </span>
              <span className="text-foreground/85">{a.text}</span>
            </li>
          ))}
        </ol>
      )}
      {caption && (
        <figcaption className="border-t border-border bg-muted/30 px-4 py-2 text-center text-xs italic text-muted-foreground">
          {caption}
        </figcaption>
      )}
    </figure>
  );
}

/* ─────────────────────────────────────────────────────────────────────
 * 6. Quiz — question unique à choix multiples avec feedback
 * ──────────────────────────────────────────────────────────────────── */
export function Quiz({ question, options = [], correct = 0, explanation }) {
  const [picked, setPicked] = useState(null);
  const isCorrect = picked !== null && picked === correct;

  return (
    <div
      data-testid="tutorial-quiz"
      className="rounded-lg border border-border bg-card p-5"
    >
      <div className="mb-3 text-[11px] font-semibold uppercase tracking-widest text-brand-gold">
        Quiz de validation
      </div>
      <p className="text-base font-semibold leading-snug text-foreground">
        {question}
      </p>
      <ul className="mt-4 space-y-2">
        {options.map((opt, i) => {
          const state =
            picked === null
              ? "idle"
              : i === correct
              ? "ok"
              : i === picked
              ? "bad"
              : "dim";
          return (
            <li key={i}>
              <button
                type="button"
                onClick={() => picked === null && setPicked(i)}
                disabled={picked !== null}
                data-testid={`tutorial-quiz-option-${i}`}
                className={cn(
                  "flex w-full items-center gap-3 rounded-md border px-3 py-2.5 text-left text-sm transition-colors",
                  state === "idle" &&
                    "border-border hover:border-brand-gold/60 hover:bg-accent/50",
                  state === "ok" &&
                    "border-emerald-500 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
                  state === "bad" &&
                    "border-red-500 bg-red-500/10 text-red-700 dark:text-red-300",
                  state === "dim" && "border-border opacity-60"
                )}
              >
                <span
                  className={cn(
                    "flex h-5 w-5 shrink-0 items-center justify-center rounded-full border text-[11px] font-bold",
                    state === "ok"
                      ? "border-emerald-500 bg-emerald-500 text-white"
                      : state === "bad"
                      ? "border-red-500 bg-red-500 text-white"
                      : "border-muted-foreground/40"
                  )}
                >
                  {state === "ok" ? (
                    <CheckCircle2 className="h-3.5 w-3.5" />
                  ) : state === "bad" ? (
                    <XCircle className="h-3.5 w-3.5" />
                  ) : (
                    String.fromCharCode(65 + i)
                  )}
                </span>
                <span className="flex-1">{opt}</span>
              </button>
            </li>
          );
        })}
      </ul>
      {picked !== null && (
        <div
          data-testid="tutorial-quiz-feedback"
          className={cn(
            "mt-4 rounded-md border-l-4 p-3 text-sm",
            isCorrect
              ? "border-emerald-500 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"
              : "border-red-500 bg-red-500/10 text-red-700 dark:text-red-300"
          )}
        >
          <div className="font-semibold">
            {isCorrect ? "Bonne réponse !" : "Mauvaise réponse."}
          </div>
          {explanation && <div className="mt-1 opacity-90">{explanation}</div>}
          <button
            type="button"
            onClick={() => setPicked(null)}
            className="mt-2 text-xs font-semibold underline hover:no-underline"
            data-testid="tutorial-quiz-retry"
          >
            Réessayer
          </button>
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────
 * Extras utiles pour les chapitres : section ancrée + formule + citation
 * + révélation au scroll (Chapitre 3)
 * ──────────────────────────────────────────────────────────────────── */

export function Section({ id, title, eyebrow, children }) {
  return (
    <section
      id={id}
      data-testid={`tutorial-section-${id}`}
      className="scroll-mt-20 space-y-4"
    >
      {eyebrow && (
        <div className="text-[11px] font-semibold uppercase tracking-widest text-brand-gold">
          {eyebrow}
        </div>
      )}
      <h2 className="font-judicial text-2xl font-black leading-tight tracking-tight sm:text-3xl">
        {title}
      </h2>
      <div className="space-y-4 text-sm leading-relaxed text-foreground/90">
        {children}
      </div>
    </section>
  );
}

export function Formula({ children, caption }) {
  return (
    <div className="my-2 rounded-lg border-2 border-brand-gold/50 bg-brand-blue p-6 text-center text-white shadow-md dark:bg-brand-blue/90">
      <div className="font-mono text-xl font-bold tracking-wide sm:text-2xl">
        {children}
      </div>
      {caption && (
        <div className="mt-2 text-xs italic text-brand-gold">{caption}</div>
      )}
    </div>
  );
}

export function Quote({ children, author }) {
  return (
    <blockquote className="rounded-lg border border-brand-gold/30 bg-brand-gold/10 p-5">
      <p className="font-judicial text-lg italic leading-relaxed text-foreground sm:text-xl">
        « {children} »
      </p>
      {author && (
        <footer className="mt-2 text-xs text-muted-foreground">— {author}</footer>
      )}
    </blockquote>
  );
}

/** Reveal children sequentially as the container scrolls into view. */
export function RevealList({ children, stagger = 250 }) {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);
  const items = useMemo(() => React.Children.toArray(children), [children]);

  useEffect(() => {
    if (!ref.current) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setShown(true);
            io.disconnect();
          }
        });
      },
      { threshold: 0.15 }
    );
    io.observe(ref.current);
    return () => io.disconnect();
  }, []);

  return (
    <div ref={ref} data-testid="tutorial-reveal-list">
      {items.map((child, i) => (
        <div
          key={i}
          className={cn(
            "transition-all duration-500 ease-out",
            shown
              ? "translate-y-0 opacity-100"
              : "translate-y-2 opacity-0"
          )}
          style={shown ? { transitionDelay: `${i * stagger}ms` } : undefined}
        >
          {child}
        </div>
      ))}
    </div>
  );
}

/** Inline field explainer with formula + numeric example. */
export function FieldExplainer({ label, formula, example, hint }) {
  return (
    <div className="rounded-md border border-border bg-card p-4">
      <div className="text-sm font-bold text-foreground">{label}</div>
      {formula && (
        <div className="mt-1 font-mono text-xs text-brand-blue dark:text-brand-gold">
          {formula}
        </div>
      )}
      {example && (
        <div className="mt-2 text-xs text-muted-foreground">
          <span className="font-semibold text-foreground/80">Exemple :</span>{" "}
          {example}
        </div>
      )}
      {hint && (
        <div className="mt-2 text-[11px] italic text-muted-foreground">
          {hint}
        </div>
      )}
    </div>
  );
}
